import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2 } from 'lucide-react';
import { getSuggestionLabel } from '@/utils/addonSuggestionRules';
import { addVehicleException, removeVehicleExceptionAt } from '@/utils/addonVehicleApplicability';
import type { AddonSuggestionType, VehicleApplicabilityException } from '@/types/addonSuggestions';

/**
 * CORE-BP item 2 — where a shop says which vehicles not to be asked about.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE WORDING IS PART OF THE CORRECTNESS
 * ═══════════════════════════════════════════════════════════════════════════
 * This surface must never suggest that OptiBay knows something about the
 * manufacturer. It does not. It has no OEM data, no fitment table and no
 * service information, and Slice A's audit is explicit that pretending
 * otherwise is the one failure this capability exists to avoid.
 *
 * So nothing here says "requirement", "factory", "manufacturer recommends" or
 * "verified". It says the shop is telling OptiBay not to ask — because that is
 * the only thing that is true.
 */

const FAMILIES: AddonSuggestionType[] = ['alignment', 'sas', 'adas', 'tpms'];

export function VehicleApplicabilityExceptions({
  value,
  onChange,
}: {
  value: VehicleApplicabilityException[];
  onChange: (next: VehicleApplicabilityException[]) => void;
}) {
  const [type, setType] = useState<AddonSuggestionType>('adas');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');

  const canAdd = make.trim().length > 0 && model.trim().length > 0;

  const handleAdd = () => {
    if (!canAdd) return;
    onChange(addVehicleException(value, { type, make, model }));
    setMake('');
    setModel('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Do not ask on these vehicles</CardTitle>
        <CardDescription>
          Your shop's own list. When a suggestion below would normally be raised by
          the work on a ticket, it will not be raised for these exact vehicles.
          OptiBay is not looking anything up on your behalf here — this is your
          rule, and it can only ever stop a question, never start one.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {value.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="vehicle-exceptions-empty">
            No vehicle exceptions. Suggestions are raised on every vehicle where the
            work says they apply.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="vehicle-exceptions-list">
            {value.map((e, i) => (
              <li
                key={`${e.type}-${e.make}-${e.model}-${i}`}
                className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
                data-testid={`vehicle-exception-${e.type}-${e.make}-${e.model}`}
              >
                <span className="text-sm">
                  Do not suggest{' '}
                  <span className="font-medium text-foreground">{getSuggestionLabel(e.type)}</span>{' '}
                  for{' '}
                  <span className="font-medium text-foreground">{e.make} {e.model}</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${getSuggestionLabel(e.type)} exception for ${e.make} ${e.model}`}
                  onClick={() => onChange(removeVehicleExceptionAt(value, i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="vx-type">Suggestion</Label>
            <Select value={type} onValueChange={(v) => setType(v as AddonSuggestionType)}>
              <SelectTrigger id="vx-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FAMILIES.map((f) => (
                  <SelectItem key={f} value={f}>{getSuggestionLabel(f)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="vx-make">Make</Label>
            <Input
              id="vx-make" value={make} placeholder="Toyota"
              onChange={(e) => setMake(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="vx-model">Model</Label>
            <Input
              id="vx-model" value={model} placeholder="Corolla"
              onChange={(e) => setModel(e.target.value)}
            />
          </div>
          <Button onClick={handleAdd} disabled={!canAdd}>Add exception</Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Make and model must match the vehicle on the ticket exactly, apart from
          capitals and spacing. "Corolla" will not match "Corolla Cross". If a
          ticket has no make or model recorded, the suggestion is still raised —
          not knowing which vehicle it is does not mean the work does not apply.
        </p>
      </CardContent>
    </Card>
  );
}
